import { CoralogixRum } from '@coralogix/browser'
import type { PostHogInterface } from 'posthog-js'

import {
  countFlags,
  createInstallGuard,
  onFlagFetchTrigger,
} from '@/lib/analytics/posthog-flag-shared'

export type FlagFailureReason = 'timeout' | 'fetch_error'

export type FlagMonitorEventName = 'posthog_flags_load_failed' | 'posthog_flags_load_recovered'

export interface FlagMonitorEvent {
  level: 'error' | 'info'
  name: FlagMonitorEventName
  reason?: FlagFailureReason
  flagCount: number
  /** Time since the previous state transition: how long a failed fetch ran, or how long an outage lasted. */
  elapsedMs: number
  distinctId: string | undefined
}

export const FLAG_LOAD_TIMEOUT_MS = 15_000
export const MAX_MONITOR_EVENTS = 5

export interface FlagMonitorDeps {
  emit: (event: FlagMonitorEvent) => void
  now: () => number
}

type MonitorState = 'pending' | 'failed' | 'loaded'

const alreadyInstalled = createInstallGuard()

export function installFlagMonitoring(posthog: PostHogInterface, deps: FlagMonitorDeps): void {
  if (!alreadyInstalled(posthog)) return

  let state: MonitorState = 'pending'
  let lastTransitionMs = deps.now()
  let emitted = 0
  let timer: number | undefined

  const emit = (event: Omit<FlagMonitorEvent, 'elapsedMs' | 'distinctId'>) => {
    if (emitted >= MAX_MONITOR_EVENTS) return
    emitted += 1
    deps.emit({
      ...event,
      elapsedMs: deps.now() - lastTransitionMs,
      distinctId: posthog.get_distinct_id(),
    })
  }

  const transitionTo = (next: MonitorState) => {
    state = next
    lastTransitionMs = deps.now()
  }

  // PostHog fires no callback at all for quota-limited flag responses, so every
  // fetch gets its own watchdog instead of the first success disarming it forever.
  const armWatchdog = () => {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      if (state !== 'pending') return
      emit({ level: 'error', name: 'posthog_flags_load_failed', reason: 'timeout', flagCount: 0 })
      transitionTo('failed')
    }, FLAG_LOAD_TIMEOUT_MS)
  }

  onFlagFetchTrigger(posthog, () => {
    transitionTo('pending')
    armWatchdog()
  })

  armWatchdog()

  posthog.onFeatureFlags((flags, _variants, context) => {
    const flagCount = countFlags(flags)
    window.clearTimeout(timer)
    if (context?.errorsLoading) {
      // Dedupes only consecutive failures — a successful load resets the state
      // so a later outage alerts again.
      if (state === 'failed') return
      emit({
        level: 'error',
        name: 'posthog_flags_load_failed',
        reason: 'fetch_error',
        flagCount,
      })
      transitionTo('failed')
      return
    }
    const wasFailing = state === 'failed'
    if (wasFailing) {
      emit({ level: 'info', name: 'posthog_flags_load_recovered', flagCount })
    }
    transitionTo('loaded')
  })
}

export function makeDefaultMonitorEmit(): FlagMonitorDeps['emit'] {
  return ({ level, name, ...data }) => {
    if (level === 'error') {
      CoralogixRum.error(name, data, { feature: 'ff-monitoring' })
    } else {
      CoralogixRum.info(name, data, { feature: 'ff-monitoring' })
    }
  }
}

export function installFlagMonitoringWithDefaults(posthog: PostHogInterface): void {
  installFlagMonitoring(posthog, { emit: makeDefaultMonitorEmit(), now: () => Date.now() })
}
